import { useState } from "react";
import ConfirmPopup from "./ConfirmPopup";
import { getStatusColor, getStatusIcon, getStatusText } from "./statusHelpers";

export const MultiDeleteManager = ({
  isOpen,
  onClose,
  itemsToDelete,
  deleteItemFunction,
  getItemIdentifier,
  onDeletionComplete,
  getIdKey = "id", // NEW: Prop to specify the ID field name
}) => {
  const [deletionStatus, setDeletionStatus] = useState({});
  const [isDeleting, setIsDeleting] = useState(false);
  const [overallProgress, setOverallProgress] = useState(0);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [deletionResults, setDeletionResults] = useState({
    success: 0,
    error: 0,
    warning: 0,
  });

  const totalItems = itemsToDelete.length;

  const handleConfirmDelete = async () => {
    setShowProgressModal(true);
    setIsDeleting(true);
    setDeletionStatus({});
    setOverallProgress(0);
    setDeletionResults({ success: 0, error: 0, warning: 0 });

    let successCount = 0;
    let errorCount = 0;
    let warningCount = 0;

    for (let i = 0; i < totalItems; i++) {
      const item = itemsToDelete[i];
      const itemId = item[getIdKey]; // Use the dynamic ID key
      const itemName = getItemIdentifier(item);

      try {
        setDeletionStatus((prev) => ({
          ...prev,
          [itemId]: { status: "deleting", name: itemName },
        }));

        // Make the API call and get the response
        const response = await deleteItemFunction(itemId);

        // Handle Axios response structure
        const statusCode = response.status || response.status_code;
        const responseData = response.data || response;

        if (statusCode === 200 && responseData.status === true) {
          // Success case
          successCount++;
          setDeletionStatus((prev) => ({
            ...prev,
            [itemId]: { status: "success", name: itemName },
          }));
        } else if (statusCode === 200 && responseData.status === false) {
          // Warning case
          warningCount++;
          setDeletionStatus((prev) => ({
            ...prev,
            [itemId]: {
              status: "warning",
              name: itemName,
              message: responseData.msg || "Deletion completed with warnings",
            },
          }));
        } else {
          // Other error cases
          throw new Error(
            responseData.msg || `Unexpected response: ${statusCode}`
          );
        }
      } catch (error) {
        console.error(`Failed to delete item ${itemId}:`, error);
        errorCount++;
        setDeletionStatus((prev) => ({
          ...prev,
          [itemId]: {
            status: "error",
            name: itemName,
            error: error.message || "Unknown error occurred",
          },
        }));
      } finally {
        const newProgress = ((i + 1) / totalItems) * 100;
        setOverallProgress(newProgress);
        setDeletionResults({
          success: successCount,
          error: errorCount,
          warning: warningCount,
        });
      }

      // Small delay between deletions
      await new Promise((resolve) => setTimeout(resolve, 300));
    }

    setIsDeleting(false);

    // Refetch data only if there were successful deletions or no errors/warnings
    if (
      onDeletionComplete &&
      typeof onDeletionComplete === "function" &&
      (successCount > 0 || (errorCount === 0 && warningCount === 0))
    ) {
      try {
        await onDeletionComplete();
        console.log("Data refetched successfully after deletion");
      } catch (error) {
        console.error("Failed to refetch data after deletion:", error);
      }
    }

    // Auto-close after 5 seconds to allow user to see results
    setTimeout(() => {
      if (!isDeleting) {
        handleClose();
      }
    }, 5000);
  };

  const handleClose = () => {
    if (!isDeleting) {
      setDeletionStatus({});
      setOverallProgress(0);
      setShowProgressModal(false);
      setDeletionResults({ success: 0, error: 0, warning: 0 });
      onClose();
    }
  };

  // Update status helpers to include warning state
  const getEnhancedStatusIcon = (status) => {
    if (status === "warning") {
      return (
        <div className="w-4 h-4 bg-yellow-500 rounded-full flex items-center justify-center">
          <svg
            className="w-3 h-3 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="3"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>
      );
    }
    return getStatusIcon(status);
  };

  const getEnhancedStatusText = (status) => {
    if (status === "warning") return "Warning";
    return getStatusText(status);
  };

  const getEnhancedStatusColor = (status) => {
    if (status === "warning") return "text-yellow-600";
    return getStatusColor(status);
  };

  return (
    <>
      {/* Confirmation Popup */}
      <ConfirmPopup
        isOpen={isOpen && !showProgressModal}
        onClose={handleClose}
        onConfirm={handleConfirmDelete}
        title={`Delete ${totalItems} Item${totalItems !== 1 ? "s" : ""}?`}
        message={
          <div>
            <p className="mb-3 font-semibold text-red-600">
              This action cannot be undone!
            </p>
            <p className="mb-4">
              Are you sure you want to delete {totalItems} selected item
              {totalItems !== 1 ? "s" : ""}?
            </p>
            {totalItems > 0 && (
              <ul className="list-disc list-inside text-sm text-gray-600 max-h-32 overflow-y-auto bg-gray-50 p-3 rounded border">
                {itemsToDelete.slice(0, 5).map((item, idx) => (
                  <li key={idx} className="truncate py-1">
                    {getItemIdentifier(item)}
                  </li>
                ))}
                {totalItems > 5 && (
                  <li className="text-gray-500 text-xs mt-1">
                    ...and {totalItems - 5} more
                  </li>
                )}
              </ul>
            )}
          </div>
        }
        confirmText={`Delete ${totalItems} Item${totalItems !== 1 ? "s" : ""}`}
        cancelText="Cancel"
        isConfirming={isDeleting}
        confirmButtonStyle="bg-red-600 hover:bg-red-700 focus:ring-red-500"
        size="lg"
      />

      {/* Enhanced Progress Modal */}
      {showProgressModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Deleting {totalItems} Item{totalItems !== 1 ? "s" : ""}
                </h3>
                {!isDeleting && (
                  <button
                    onClick={handleClose}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      ></path>
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* Progress Section */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">
                  Progress: {Math.round(overallProgress)}%
                </span>
                <span className="text-sm text-gray-500">
                  {deletionResults.success +
                    deletionResults.error +
                    deletionResults.warning}{" "}
                  of {totalItems} completed
                  {(deletionResults.success > 0 ||
                    deletionResults.warning > 0 ||
                    deletionResults.error > 0) && (
                    <span>
                      ({deletionResults.success} successful,{" "}
                      {deletionResults.warning} warnings,{" "}
                      {deletionResults.error} failed)
                    </span>
                  )}
                </span>
              </div>

              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${overallProgress}%` }}
                ></div>
              </div>
            </div>

            {/* Status List */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="space-y-2">
                {itemsToDelete.map((item) => {
                  const itemId = item[getIdKey];
                  const statusData = deletionStatus[itemId];
                  const status = statusData?.status || "pending";
                  const name = statusData?.name || getItemIdentifier(item);
                  const message = statusData?.message || statusData?.error;

                  return (
                    <div
                      key={itemId}
                      className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center space-x-3 min-w-0 flex-1">
                        {getEnhancedStatusIcon(status)}
                        <div className="min-w-0 flex-1">
                          <span className="text-sm font-medium text-gray-900 truncate block">
                            {name}
                          </span>
                          {message && (
                            <span className="text-xs text-gray-500 truncate block mt-1">
                              {message}
                            </span>
                          )}
                        </div>
                      </div>
                      <span
                        className={`text-xs font-medium ${getEnhancedStatusColor(
                          status
                        )}`}
                      >
                        {getEnhancedStatusText(status)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              {overallProgress === 100 && !isDeleting && (
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    <p>Deletion completed.</p>
                    {deletionResults.success > 0 && (
                      <p className="text-green-600">
                        ✓ {deletionResults.success} item(s) deleted successfully
                      </p>
                    )}
                    {deletionResults.warning > 0 && (
                      <p className="text-yellow-600">
                        ⚠ {deletionResults.warning} item(s) completed with
                        warnings
                      </p>
                    )}
                    {deletionResults.error > 0 && (
                      <p className="text-red-600">
                        ✗ {deletionResults.error} item(s) failed to delete
                      </p>
                    )}
                    {(deletionResults.success > 0 ||
                      deletionResults.warning === 0) && (
                      <p className="text-blue-600 mt-1">Refreshing data...</p>
                    )}
                  </div>
                  <button
                    onClick={handleClose}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    Close
                  </button>
                </div>
              )}
              {isDeleting && (
                <p className="text-sm text-gray-600 text-center">
                  Please wait while items are being deleted...
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
